import { useState, useEffect, useCallback, useRef } from "react";
import { CollectionsScreen } from "./components/CollectionsScreen";
import { VictoryScreen } from "./components/VictoryScreen";
import { SettingsMenu } from "./components/SettingsMenu";
import { GameManager } from "./GameManager";
import { CollectionManager } from "./CollectionManager";
import { pokiSDK } from "./PokiSDK";
import { imagePreloader } from "./ImagePreloader";
import { Application } from "pixi.js";
import collectionsData from "./collections.json";
import type { Collection, CollectionsConfig } from "./types";
import "./App.css";

type Screen = "collections" | "gameplay" | "victory";

interface GameState {
  currentScreen: Screen;
  collections: Collection[];
  currentCollectionId: string;
  currentCollectionName: string;
  currentLevel: number;
  totalLevels: number;
  coins: number;
  completionData: { [id: string]: { completed: number; total: number } };
  coinsEarned: number;
  hasNextLevel: boolean;
}

export function App() {
  const [gameState, setGameState] = useState<GameState>({
    currentScreen: "collections",
    collections: [],
    currentCollectionId: "",
    currentCollectionName: "",
    currentLevel: 1,
    totalLevels: 10,
    coins: 0,
    completionData: {},
    coinsEarned: 10,
    hasNextLevel: true,
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const appRef = useRef<Application | null>(null);
  const gameManagerRef = useRef<GameManager | null>(null);
  const collectionManagerRef = useRef<CollectionManager | null>(null);
  const initRef = useRef(false);

  const updateGameState = useCallback(() => {
    const collectionManager = collectionManagerRef.current;
    const gameManager = gameManagerRef.current;
    if (!collectionManager) return;

    const collections = collectionManager.getCollections();
    const currentCollection = collectionManager.getCurrentCollection();
    const coins = collectionManager.getCoins();

    const completionData: {
      [id: string]: { completed: number; total: number };
    } = {};
    for (const collection of collections) {
      const progress = collectionManager.getCollectionProgress(collection.id);
      completionData[collection.id] = {
        completed: progress?.completedLevels.length || 0,
        total: collection.levels.length,
      };
    }

    setGameState((prev) => ({
      ...prev,
      collections,
      currentCollectionId: currentCollection?.id || "",
      currentCollectionName: currentCollection?.name || "",
      totalLevels: currentCollection?.levels.length || 10,
      coins,
      completionData,
      currentLevel: gameManager?.getCurrentLevel() || 1,
    }));
  }, []);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      // Initialize Poki SDK
      await pokiSDK.init();

      // Initialize Pixi app
      const app = new Application();
      appRef.current = app;

      await app.init({
        background: "#e8f4fc",
        width: 1280,
        height: 720,
        resizeTo: window,
        autoDensity: true,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
      });

      const container = document.getElementById("game-container");
      if (container) {
        container.appendChild(app.canvas);
      }

      // Initialize managers
      const gameManager = new GameManager(app);
      const collectionManager = new CollectionManager();
      gameManagerRef.current = gameManager;
      collectionManagerRef.current = collectionManager;

      // Load collections
      const collectionsConfig = collectionsData as CollectionsConfig;
      collectionManager.loadCollections(collectionsConfig);

      // Set up victory callback
      gameManager.setVictoryCallback(() => {
        const cm = collectionManagerRef.current;
        const gm = gameManagerRef.current;
        if (!cm || !gm) return;

        const currentLevel = gm.getCurrentLevel();
        const currentCollection = cm.getCurrentCollection();
        if (!currentCollection) return;

        // Don't award coins during replay
        const isReplaying = cm.isReplaying();
        const coinsEarned = isReplaying ? 0 : 10;

        if (!isReplaying) {
          cm.completeLevel(currentCollection.id, currentLevel, coinsEarned);
        }

        // During replay, always show next level if there are more levels
        const hasNextLevel = currentLevel < currentCollection.levels.length;

        pokiSDK.gameplayStop();

        setGameState((prev) => ({
          ...prev,
          currentScreen: "victory",
          coinsEarned,
          hasNextLevel,
          currentLevel,
        }));

        updateGameState();
      });

      updateGameState();

      // Resume current collection if in progress, or start first incomplete collection
      const currentCollection = collectionManager.getCurrentCollection();
      let collectionToPlay: Collection | null = null;

      if (currentCollection) {
        const progress = collectionManager.getCollectionProgress(
          currentCollection.id,
        );
        const isComplete =
          progress &&
          progress.completedLevels.length >= currentCollection.levels.length;

        if (!isComplete) {
          collectionToPlay = currentCollection;
        }
      }

      // If no current collection or it's complete, try to find first incomplete unlocked collection
      if (!collectionToPlay) {
        const allCollections = collectionManager.getCollections();
        for (const collection of allCollections) {
          if (collection.unlocked) {
            const progress = collectionManager.getCollectionProgress(
              collection.id,
            );
            const isComplete =
              progress &&
              progress.completedLevels.length >= collection.levels.length;

            if (!isComplete) {
              collectionToPlay = collection;
              break;
            }
          }
        }
      }

      // Start playing the collection if found
      if (collectionToPlay) {
        const success = collectionManager.setCurrentCollection(
          collectionToPlay.id,
        );
        if (success) {
          const progress = collectionManager.getCollectionProgress(
            collectionToPlay.id,
          );
          await gameManager.loadLevels(collectionToPlay.levels);
          if (progress) {
            gameManager.setCurrentLevel(progress.currentLevel);
          }
          gameManager.setCollectionName(collectionToPlay.name);
          await gameManager.initLevel();
          gameManager.updateLevelDisplay();
          gameManager.show();
          pokiSDK.gameplayStart();
          setGameState((prev) => ({
            ...prev,
            currentScreen: "gameplay",
            currentCollectionId: collectionToPlay!.id,
            currentCollectionName: collectionToPlay!.name,
            currentLevel: gameManager.getCurrentLevel(),
            totalLevels: collectionToPlay!.levels.length,
          }));
        }
      }
      // If no collection to play, stay on collections screen (all complete)

      // Tell Poki loading is done
      pokiSDK.gameLoadingFinished();

      setIsInitialized(true);

      // After first level loads, preload first puzzle of each collection
      // This ensures fast switching between collections
      imagePreloader.preloadCollectionPreviews(collectionsConfig.collections);
    };

    init();

    return () => {
      // Cleanup - but be careful with StrictMode
      const app = appRef.current;
      if (app && app.renderer) {
        app.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, [updateGameState]);

  const handleSelectCollection = async (collectionId: string) => {
    const collectionManager = collectionManagerRef.current;
    const gameManager = gameManagerRef.current;
    if (!collectionManager || !gameManager) return;

    const success = collectionManager.setCurrentCollection(collectionId);
    if (success) {
      const collection = collectionManager.getCollection(collectionId);
      if (collection) {
        await gameManager.loadLevels(collection.levels);

        const progress = collectionManager.getCollectionProgress(collectionId);
        if (progress) {
          gameManager.setCurrentLevel(progress.currentLevel);
        }

        // Set collection name and update Pixi UI
        gameManager.setCollectionName(collection.name);
        await gameManager.initLevel();
        gameManager.updateLevelDisplay();
        gameManager.show();
        pokiSDK.gameplayStart();

        setGameState((prev) => ({
          ...prev,
          currentScreen: "gameplay",
          currentCollectionId: collectionId,
          currentCollectionName: collection.name,
          currentLevel: gameManager.getCurrentLevel(),
          totalLevels: collection.levels.length,
        }));
      }
    }
  };

  const handlePurchaseCollection = (collectionId: string) => {
    const collectionManager = collectionManagerRef.current;
    if (!collectionManager) return;

    const success = collectionManager.purchaseCollection(collectionId);
    if (success) {
      updateGameState();
      handleSelectCollection(collectionId);
    }
  };

  const handleReplayCollection = async (collectionId: string) => {
    const collectionManager = collectionManagerRef.current;
    const gameManager = gameManagerRef.current;
    if (!collectionManager || !gameManager) return;

    // Start replay mode
    collectionManager.startReplay(collectionId);

    const collection = collectionManager.getCollection(collectionId);
    if (collection) {
      await gameManager.loadLevels(collection.levels);
      gameManager.setCurrentLevel(1);

      // Set collection name and update Pixi UI
      gameManager.setCollectionName(collection.name);
      await gameManager.initLevel();
      gameManager.updateLevelDisplay();
      gameManager.show();
      pokiSDK.gameplayStart();

      setGameState((prev) => ({
        ...prev,
        currentScreen: "gameplay",
        currentCollectionId: collectionId,
        currentCollectionName: collection.name,
        currentLevel: 1,
        totalLevels: collection.levels.length,
      }));
    }
  };

  const handleBack = () => {
    const gameManager = gameManagerRef.current;
    const collectionManager = collectionManagerRef.current;
    if (gameManager) {
      gameManager.hide();
    }
    if (collectionManager) {
      collectionManager.endReplay();
    }
    updateGameState();
    setGameState((prev) => ({ ...prev, currentScreen: "collections" }));
  };

  const handleNextLevel = async () => {
    const gameManager = gameManagerRef.current;
    if (!gameManager) return;

    // Show commercial ad before next level
    pokiSDK.gameplayStop();
    await pokiSDK.commercialBreak();

    setGameState((prev) => ({ ...prev, currentScreen: "gameplay" }));

    const hasNext = await gameManager.nextLevel();
    if (hasNext) {
      gameManager.updateLevelDisplay();
      pokiSDK.gameplayStart();
      setGameState((prev) => ({
        ...prev,
        currentLevel: gameManager.getCurrentLevel(),
      }));
    } else {
      handleBack();
    }
  };

  const handleCollectionsFromVictory = () => {
    handleBack();
  };

  if (!isInitialized) {
    return <div className="loading">Loading...</div>;
  }

  const showBackButton = gameState.currentScreen === "gameplay";
  const hideSettings =
    gameState.currentScreen === "victory" ||
    gameState.currentScreen === "collections";
  const fixedSettings = gameState.currentScreen === "gameplay";

  return (
    <>
      <SettingsMenu
        showBackButton={showBackButton}
        onBack={handleBack}
        hidden={hideSettings}
        fixed={fixedSettings}
      />

      {gameState.currentScreen === "collections" && (
        <CollectionsScreen
          collections={gameState.collections}
          currentCoins={gameState.coins}
          currentCollectionId={gameState.currentCollectionId}
          completionData={gameState.completionData}
          onSelectCollection={handleSelectCollection}
          onPurchaseCollection={handlePurchaseCollection}
          onReplayCollection={handleReplayCollection}
        />
      )}

      {gameState.currentScreen === "victory" && (
        <VictoryScreen
          currentLevel={gameState.currentLevel}
          totalLevels={gameState.totalLevels}
          coinsEarned={gameState.coinsEarned}
          totalCoins={gameState.coins}
          hasNextLevel={gameState.hasNextLevel}
          onNextLevel={handleNextLevel}
          onCollections={handleCollectionsFromVictory}
        />
      )}
    </>
  );
}
